import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAd } from '@/contexts/AdContext';
import Pressable from '@/components/base/Pressable';

export default function AdGate() {
  const { resolvedTheme } = useTheme();
  const { showAdGate, adState, currentReward, countdownSeconds, skipAd, completeAd, isPremium } = useAd();
  const isDark = resolvedTheme === 'dark';

  // Keyboard escape to dismiss
  useEffect(() => {
    if (!showAdGate) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipAd();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showAdGate, skipAd]);

  const t = {
    overlay: isDark ? 'bg-black/60' : 'bg-black/40',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.06]' : 'border-neutral-200',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    accentBg: isDark ? 'bg-white/[0.06]' : 'bg-neutral-100',
    ringTrack: isDark ? 'stroke-neutral-800' : 'stroke-neutral-200',
    ringFill: isDark ? 'stroke-white' : 'stroke-neutral-900',
    successBg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
    successText: isDark ? 'text-emerald-400' : 'text-emerald-600',
    successBorder: isDark ? 'border-emerald-500/20' : 'border-emerald-200',
    ctaBg: isDark ? 'bg-white text-black' : 'bg-black text-white',
    secondaryBg: isDark ? 'bg-white/[0.04] text-white border-white/[0.08]' : 'bg-neutral-100 text-neutral-900 border-neutral-200',
  };

  const progress = Math.max(0, (6 - countdownSeconds) / 6);
  const circumference = 2 * Math.PI * 48;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <AnimatePresence>
      {showAdGate && (
        <motion.div
          className={`fixed inset-0 z-[100] flex items-center justify-center p-6 ${t.overlay} backdrop-blur-sm`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={skipAd}
        >
          <motion.div
            className={`w-full max-w-[360px] rounded-3xl border ${t.cardBorder} ${t.cardBg} p-6 shadow-2xl transition-colors duration-500`}
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 12 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon / Orb */}
            <div className="flex justify-center mb-5">
              <div className="relative w-24 h-24 flex items-center justify-center">
                {/* Background ring */}
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="48"
                    fill="none"
                    strokeWidth="3"
                    className={t.ringTrack}
                  />
                  <motion.circle
                    cx="50"
                    cy="50"
                    r="48"
                    fill="none"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className={t.ringFill}
                    strokeDasharray={circumference}
                    animate={{ strokeDashoffset }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </svg>

                {/* Inner icon */}
                <motion.div
                  className={`w-14 h-14 rounded-2xl ${t.accentBg} flex items-center justify-center relative z-10`}
                  animate={adState === 'watching' ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={{ duration: 0.6, repeat: adState === 'watching' ? Infinity : 0 }}
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    {adState === 'completed' ? (
                      <i className="ri-check-line text-emerald-500 text-2xl" />
                    ) : (
                      <i className={`ri-movie-ai-line ${isDark ? 'text-white' : 'text-neutral-900'} text-2xl`} />
                    )}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-1">
              <h3 className={`text-base font-bold ${t.textPrimary}`}>
                {adState === 'completed'
                  ? 'Reward Unlocked!'
                  : currentReward?.label ?? 'Unlock Feature'}
              </h3>
            </div>

            {/* Description */}
            <div className="text-center mb-6">
              {adState === 'completed' ? (
                <p className={`text-sm ${t.textSecondary}`}>
                  {currentReward?.grant ?? 'Your reward is ready.'}
                </p>
              ) : adState === 'watching' ? (
                <p className={`text-sm ${t.textSecondary}`}>
                  Finalizing your reward...
                </p>
              ) : (
                <p className={`text-sm ${t.textSecondary}`}>
                  Watch a short message to{' '}
                  <span className="font-semibold">{currentReward?.grant ?? 'unlock this feature'}</span>.
                  No interruptions to your study flow.
                </p>
              )}
            </div>

            {/* Countdown badge */}
            {adState === 'countdown' && (
              <div className="flex justify-center mb-5">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-neutral-50 border-neutral-200'}`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-time-line ${t.textMuted} text-sm`} />
                  </div>
                  <span className={`text-sm font-semibold ${t.textPrimary}`}>
                    {countdownSeconds}s remaining
                  </span>
                </div>
              </div>
            )}

            {/* Success badge */}
            {adState === 'completed' && (
              <motion.div
                className={`flex justify-center mb-5`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${t.successBg} ${t.successBorder}`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-check-line ${t.successText} text-sm`} />
                  </div>
                  <span className={`text-sm font-semibold ${t.successText}`}>
                    {currentReward?.grant ?? 'Reward granted'}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Premium upsell hint */}
            {!isPremium && adState !== 'completed' && (
              <div className="text-center mb-5">
                <p className={`text-[11px] ${t.textMuted}`}>
                  Want to skip ads forever?{' '}
                  <button
                    onClick={skipAd}
                    className="font-semibold underline cursor-pointer"
                  >
                    Go Premium
                  </button>
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2.5">
              {adState === 'countdown' && (
                <>
                  <Pressable
                    onClick={completeAd}
                    className={`w-full py-3 rounded-xl text-sm font-semibold ${t.ctaBg} flex items-center justify-center gap-2`}
                    scale={0.97}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-circle-line text-sm" />
                    </div>
                    <span className="whitespace-nowrap">Continue Now</span>
                  </Pressable>
                  <Pressable
                    onClick={skipAd}
                    className={`w-full py-3 rounded-xl text-sm font-semibold border ${t.secondaryBg} flex items-center justify-center gap-2`}
                    scale={0.97}
                  >
                    <span className="whitespace-nowrap">No Thanks</span>
                  </Pressable>
                </>
              )}

              {adState === 'watching' && (
                <div className={`w-full py-3 rounded-xl text-sm font-semibold border ${t.secondaryBg} flex items-center justify-center gap-2`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <motion.div
                      className={`w-3 h-3 rounded-full border-2 ${isDark ? 'border-white/30 border-t-white' : 'border-neutral-300 border-t-neutral-900'}`}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                    />
                  </div>
                  <span className="whitespace-nowrap">Processing reward...</span>
                </div>
              )}

              {adState === 'completed' && (
                <Pressable
                  onClick={completeAd}
                  className={`w-full py-3 rounded-xl text-sm font-semibold ${t.ctaBg} flex items-center justify-center gap-2`}
                  scale={0.97}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                  <span className="whitespace-nowrap">Continue</span>
                </Pressable>
              )}
            </div>

            {/* Subtle footer note */}
            <p className={`text-center text-[10px] ${t.textFaint} mt-4 leading-relaxed`}>
              Rewarded ads help keep LAVYN free for students worldwide.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}